import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Calendar, CheckSquare, Edit2, Plus, Save, Trash2, User } from 'lucide-react';
import type { ComiteAction, ComiteReunionComplete } from '../../services/comiteService';
import { addAction, deleteAction, updateAction } from '../../services/comiteService';
import { getPartiesPrenantes, type PartiePrenante } from '../../services/partiesPrenantesService';
import { formatDateLocal } from '../../utils/dateFormatter';

type ToastFn = (message: string, type: 'success' | 'error') => void;

interface ActionsTabProps {
  reunion: ComiteReunionComplete;
  onUpdate: () => void;
  showToast: ToastFn;
}

export default function ActionsTab({ reunion, onUpdate, showToast }: ActionsTabProps) {
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editData, setEditData] = useState<Partial<ComiteAction>>({});
  const [partiesPrenantes, setPartiesPrenantes] = useState<PartiePrenante[]>([]);
  const [newAction, setNewAction] = useState<Partial<ComiteAction>>({
    titre: '',
    description: '',
    responsable: '',
    echeance: '',
    priorite: 'Moyenne',
    statut: 'Non démarré',
  });

  useEffect(() => {
    (async () => {
      try {
        // Contacts disponibles = client de la réunion (+ BZ, selon la logique backend)
        const pp = await getPartiesPrenantes(reunion.client_id);
        setPartiesPrenantes(pp);
      } catch (e) {
        console.error(e);
      }
    })();
  }, [reunion.client_id]);

  const actionsByStatus = useMemo(() => {
    const all = reunion.actions || [];
    return {
      'Non démarré': all.filter((a) => a.statut === 'Non démarré'),
      'En cours': all.filter((a) => a.statut === 'En cours'),
      'Complété': all.filter((a) => a.statut === 'Complété'),
      'Bloqué': all.filter((a) => a.statut === 'Bloqué'),
    } as Record<string, ComiteAction[]>;
  }, [reunion.actions]);

  const totalActions = reunion.actions?.length || 0;
  const completedActions = actionsByStatus['Complété']?.length || 0;
  const progressPercent = totalActions > 0 ? Math.round((completedActions / totalActions) * 100) : 0;

  const handleAdd = async () => {
    try {
      if (!reunion.id) return;
      if (!newAction.titre || !newAction.responsable) {
        return showToast('Titre et responsable requis', 'error');
      }
      await addAction({ ...newAction, reunion_id: reunion.id } as any);
      showToast('Action ajoutée!', 'success');
      setShowAddForm(false);
      setNewAction({
        titre: '',
        description: '',
        responsable: '',
        echeance: '',
        priorite: 'Moyenne',
        statut: 'Non démarré',
      });
      onUpdate();
    } catch (e) {
      console.error(e);
      showToast("Erreur lors de l'ajout", 'error');
    }
  };

  const startEdit = (action: ComiteAction) => {
    setEditingId(action.id || null);
    const dateOnly = action.echeance ? action.echeance.split('T')[0] : '';
    setEditData({
      titre: action.titre,
      description: action.description,
      responsable: action.responsable,
      echeance: dateOnly,
      priorite: action.priorite,
      statut: action.statut,
    });
  };

  const saveEdit = async () => {
    try {
      if (!editingId) return;
      if (!editData.titre || !editData.responsable) return showToast('Titre et responsable requis', 'error');
      await updateAction(editingId, editData);
      showToast('Action mise à jour!', 'success');
      setEditingId(null);
      setEditData({});
      onUpdate();
    } catch (e) {
      console.error(e);
      showToast('Erreur lors de la mise à jour', 'error');
    }
  };

  const remove = async (actionId: number, titre: string) => {
    if (!confirm(`Supprimer "${titre}"?`)) return;
    try {
      await deleteAction(actionId);
      showToast('Action supprimée!', 'success');
      onUpdate();
    } catch (e) {
      console.error(e);
      showToast('Erreur lors de la suppression', 'error');
    }
  };

  const handleStatusChange = async (actionId: number, newStatus: string) => {
    try {
      await updateAction(actionId, { statut: newStatus });
      showToast('Statut mis à jour!', 'success');
      onUpdate();
    } catch (e) {
      console.error(e);
      showToast('Erreur lors de la mise à jour', 'error');
    }
  };

  return (
    <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-2xl font-bold text-gray-800 flex items-center gap-3">
            <CheckSquare className="w-7 h-7 text-indigo-600" />
            Actions & TODOs
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            {completedActions}/{totalActions} complétées • {progressPercent}% de progression
          </p>
        </div>
        <button
          onClick={() => setShowAddForm(true)}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Nouvelle action
        </button>
      </div>

      {totalActions > 0 && (
        <div className="bg-gray-200 rounded-full h-4 overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${progressPercent}%` }}
            transition={{ duration: 0.5 }}
            className="bg-gradient-to-r from-indigo-500 to-purple-600 h-full flex items-center justify-center text-xs text-white font-bold"
          >
            {progressPercent}%
          </motion.div>
        </div>
      )}

      {showAddForm && (
        <div className="bg-indigo-50 border-2 border-indigo-300 rounded-xl p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Titre *</label>
            <input
              value={newAction.titre || ''}
              onChange={(e) => setNewAction({ ...newAction, titre: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
            <textarea
              rows={2}
              value={newAction.description || ''}
              onChange={(e) => setNewAction({ ...newAction, description: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Responsable *</label>
              <select
                value={newAction.responsable || ''}
                onChange={(e) => setNewAction({ ...newAction, responsable: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              >
                <option value="">Sélectionner...</option>
                {partiesPrenantes.map((pp) => (
                  <option key={pp.id} value={`${pp.prenom} ${pp.nom}`}>
                    {pp.prenom} {pp.nom}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Échéance</label>
              <input
                type="date"
                value={(newAction.echeance as any) || ''}
                onChange={(e) => setNewAction({ ...newAction, echeance: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Priorité</label>
              <select
                value={newAction.priorite || 'Moyenne'}
                onChange={(e) => setNewAction({ ...newAction, priorite: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              >
                <option value="Basse">Basse</option>
                <option value="Moyenne">Moyenne</option>
                <option value="Haute">Haute</option>
                <option value="Critique">Critique</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Statut</label>
              <select
                value={newAction.statut || 'Non démarré'}
                onChange={(e) => setNewAction({ ...newAction, statut: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              >
                <option value="Non démarré">Non démarré</option>
                <option value="En cours">En cours</option>
                <option value="Complété">Complété</option>
                <option value="Bloqué">Bloqué</option>
              </select>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={handleAdd} className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">
              Ajouter
            </button>
            <button onClick={() => setShowAddForm(false)} className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300">
              Annuler
            </button>
          </div>
        </div>
      )}

      {totalActions === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-xl">
          <CheckSquare className="w-16 h-16 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">Aucune action pour le moment</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {Object.entries(actionsByStatus).map(([status, actions]) => {
            if (!actions.length) return null;
            return (
              <div key={status} className="bg-white rounded-xl shadow-sm border p-4">
                <h4 className="font-bold text-gray-800 mb-3 flex items-center justify-between">
                  {status}
                  <span className="bg-gray-200 text-gray-700 text-xs px-2 py-1 rounded-full">{actions.length}</span>
                </h4>
                <div className="space-y-3">
                  {actions.map((action) => {
                    const isEditing = editingId === action.id;
                    return (
                      <div key={action.id} className="bg-gray-50 border border-gray-200 rounded-lg p-3 group">
                        {isEditing ? (
                          <div className="space-y-3">
                            <input
                              value={editData.titre || ''}
                              onChange={(e) => setEditData({ ...editData, titre: e.target.value })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                            />
                            <textarea
                              rows={2}
                              value={editData.description || ''}
                              onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                            />
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                              <input
                                value={editData.responsable || ''}
                                onChange={(e) => setEditData({ ...editData, responsable: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                                placeholder="Responsable"
                              />
                              <input
                                type="date"
                                value={(editData.echeance as any) || ''}
                                onChange={(e) => setEditData({ ...editData, echeance: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                              />
                            </div>
                            <div className="flex items-center gap-2">
                              <button onClick={saveEdit} className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">
                                <Save className="w-4 h-4" />
                                Sauvegarder
                              </button>
                              <button onClick={() => { setEditingId(null); setEditData({}); }} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300">
                                Annuler
                              </button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1">
                                <div className="font-bold text-gray-800">{action.titre}</div>
                                {action.description && <div className="text-sm text-gray-600 mt-1">{action.description}</div>}
                                <div className="flex items-center justify-between mt-2 text-xs text-gray-600">
                                  <div className="flex items-center gap-2">
                                    <span className="flex items-center gap-1"><User className="w-3 h-3" />{action.responsable}</span>
                                    {action.echeance && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{formatDateLocal(String(action.echeance), 'fr-CA')}</span>}
                                  </div>
                                  {action.id && (
                                    <select
                                      value={action.statut}
                                      onChange={(e) => handleStatusChange(action.id!, e.target.value)}
                                      className="px-2 py-1 text-xs border border-gray-300 rounded bg-white"
                                    >
                                      <option value="Non démarré">Non démarré</option>
                                      <option value="En cours">En cours</option>
                                      <option value="Complété">Complété</option>
                                      <option value="Bloqué">Bloqué</option>
                                    </select>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-1">
                                <button onClick={() => startEdit(action)} className="opacity-0 group-hover:opacity-100 transition-opacity p-2 rounded-lg hover:bg-indigo-100 text-indigo-600" title="Modifier">
                                  <Edit2 className="w-4 h-4" />
                                </button>
                                {action.id && (
                                  <button onClick={() => remove(action.id!, action.titre)} className="opacity-0 group-hover:opacity-100 transition-opacity p-2 rounded-lg hover:bg-red-100 text-red-600" title="Supprimer">
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                )}
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </motion.div>
  );
}

